import { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";

const UpdatePositionPopup = ({ position, onClose, onUpdate }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      job_title: position.job_title,
      description: position.job_description,
      other_details: position.other_info,
      required_qualifications: position.required_qualifications,
      industry_name: position.industry_name,
      required_experience: position.required_experience,
      number_of_position: position.number_of_position
    }
  });

  const [loader, setLoader] = useState(false);

  const onSubmit = async (data) => {

    setLoader(true);
    try {
      const payload = {
        "company_position_pk": position.company_position_pk,
        "job_title": data.job_title,
        "job_description": data.description,
        "other_info": data.other_details,
        "required_qualifications": data.required_qualifications,
        "industry_name": data.industry_name,
        "required_experience": data.required_experience,
        "number_of_position": data.number_of_position,
        "is_active": true
      }
      await onUpdate(payload);
      onClose();
    } catch (error) {
      console.error("Error updating position:", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Update Position</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block font-medium mb-1 text-base">
                Position Title <span className="text-red-500">*</span>
              </label>
              <input
                {...register("job_title", {
                  required: "Position title is required",
                  validate: (value) =>
                    value.trim() !== "" ||
                    "Position title cannot be empty or just spaces",
                })}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.job_title && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.job_title.message}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Description <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-500 mb-2">(Add a short description of the job profile to help others understand the role)</p>
              <textarea
                {...register("description", {
                  required: "Description is required",
                  validate: (value) =>
                    value.trim() !== "" ||
                    "Description cannot be empty or just spaces",
                })}
                rows={7}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Other Details
              </label>
              <input
                {...register("other_details")}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Required Qualifications <span className="text-red-500">*</span>
              </label>
              <input
                {...register("required_qualifications", {
                  required: "Please enter required qualifications"
                })}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.required_qualifications && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.required_qualifications.message}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Industry <span className="text-red-500">*</span>
              </label>
              <input
                {...register("industry_name", {
                  required: "Please enter industry."
                })}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.industry_name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.industry_name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Required Experience <span className="text-red-500">*</span>
              </label>
              <input
                {...register("required_experience", {
                  required: "Please enter required experience.",
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Only numeric values are allowed",
                  },
                })}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.required_experience && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.required_experience.message}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium mb-1 text-base">
                Number Of Positions <span className="text-red-500">*</span>
              </label>
              <input
                {...register("number_of_position", {
                  required: "Please enter number of positions.",
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Only numeric values are allowed",
                  },
                })}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 transition placeholder:text-sm"
              />
              {errors.number_of_position && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.number_of_position.message}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loader}
                className={`bg-gradient-to-r from-violet-500 to-violet-700 hover:from-violet-600 hover:to-violet-800 text-white px-6 py-2 rounded-md shadow-md font-semibold transition-all ${loader ? "opacity-60 cursor-not-allowed" : ""
                  }`}
              >
                {loader ? "Updating..." : "Update Position"}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default UpdatePositionPopup;